<template>
  <div class="w-full flex flex-col">
    <div class="m-2 p-4 bg-white rounded shadow space-y-4 max-w-5xl" v-if="course">
      <div class="flex flex-wrap items-center gap-3">
        <router-link class="text-sm text-primary underline" to="/Matrices/Courses">Courses</router-link>
        <h1 class="text-lg font-medium text-primary flex-grow">{{ course.Title }}</h1>
        <span class="text-sm uppercase tracking-wide text-gray-500">{{ course.Status === "published" ? "Published" : "Draft" }}</span>
      </div>
      <p class="text-sm text-gray-600">{{ completionCount }} completion{{ completionCount === 1 ? "" : "s" }}</p>
      <ProcedureCheckPanel :check="procedureCheck" />
      <div class="flex flex-wrap gap-2">
        <router-link class="px-3 py-2 border rounded" :to="`/Matrices/Courses/${course.ID}/play?preview=1`">Preview</router-link>
        <router-link class="px-3 py-2 bg-primary text-white rounded" v-if="course.Status === 'published'" :to="`/Matrices/Courses/${course.ID}/play`">Open player</router-link>
        <button class="px-3 py-2 border rounded" type="button" v-if="course.Status !== 'published'" :disabled="!savable" @click="publish">Publish</button>
        <button class="px-3 py-2 border rounded" type="button" v-else @click="unpublish">Unpublish</button>
      </div>
      <label class="block space-y-1">
        <span class="text-sm font-medium">Regenerate with a note</span>
        <textarea class="w-full border rounded p-2 min-h-20" v-model="revisionNote"></textarea>
      </label>
      <input class="block text-sm" type="file" multiple accept=".pdf,.docx,.pptx,.txt,.md,.markdown" @change="onFiles" />
      <button class="px-4 py-2 bg-primary text-white rounded disabled:bg-gray-300" type="button" :disabled="loading" @click="regenerate">
        {{ loading ? "Checking handbooks…" : "Regenerate" }}
      </button>
      <label class="block space-y-1">
        <span class="text-sm font-medium">Course schema</span>
        <textarea class="w-full border rounded p-2 min-h-48 font-mono text-xs" :value="jsonText" @input="onJson"></textarea>
      </label>
      <p class="text-sm text-red-700" v-if="error">{{ error }}</p>
      <ul class="text-sm text-amber-800 list-disc pl-5" v-if="warnings.length">
        <li v-for="warning in warnings" :key="warning">{{ warning }}</li>
      </ul>
      <div class="flex gap-2">
        <button class="px-4 py-2 border rounded disabled:bg-gray-100" type="button" :disabled="loading" @click="recheck">Recheck handbooks</button>
        <button class="px-4 py-2 bg-primary text-white rounded disabled:bg-gray-300" type="button" :disabled="!savable || saving" @click="save">Save draft</button>
      </div>
    </div>
    <p class="m-4 text-sm text-gray-600" v-else-if="missing">This course was not found.</p>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { useRoute } from "vue-router";
import { useStore } from "vuex";
import { db } from "../../../db.js";
import { validateCourseSchema } from "../../../../functions/functions-common/agents/agents/generateTrainingCourse/schema.mjs";
import ProcedureCheckPanel from "./ProcedureCheckPanel.vue";
import { canSaveCourse, generateCourse, readFileAsBase64, setCourseStatus, updateCourse } from "./courseApi.js";

const route = useRoute();
const store = useStore();
const revisionNote = ref("");
const files = ref([]);
const jsonText = ref("");
const procedureCheck = ref(null);
const warnings = ref([]);
const error = ref("");
const loading = ref(false);
const saving = ref(false);
const loaded = ref(false);
const dirty = ref(false);

const course = computed(() => {
  const id = route.params.id;
  const record = store.state["Home/Courses"]?.[id];
  return record ? { ...record, ID: record.ID || id } : null;
});
const missing = computed(() => loaded.value && !course.value);
const parsed = computed(() => {
  try {
    return { schema: JSON.parse(jsonText.value), error: "" };
  } catch (err) {
    return { schema: null, error: "The schema is not valid JSON." };
  }
});
const schemaResult = computed(() => parsed.value.schema ? validateCourseSchema(parsed.value.schema) : null);
const savable = computed(() => !dirty.value && canSaveCourse(schemaResult.value, procedureCheck.value));
const completionCount = computed(() => Object.keys(course.value?.Completions || {}).length);

function onFiles(event) {
  files.value = Array.from(event.target.files || []);
}

function onJson(event) {
  jsonText.value = event.target.value;
  dirty.value = true;
  procedureCheck.value = null;
  error.value = parsed.value.error || schemaResult.value?.errors?.[0] || "";
}

async function regenerate() {
  error.value = "";
  loading.value = true;
  try {
    const localWarnings = [];
    const encoded = [];
    for (const file of files.value) {
      if (file.size > 1000000) {
        localWarnings.push(`${file.name} is too large and was skipped.`);
        continue;
      }
      encoded.push(await readFileAsBase64(file));
    }
    const result = await generateCourse({
      mode: "generate",
      prompt: course.value?.SourcePrompt || course.value?.Title || "Revise this course.",
      revisionNote: revisionNote.value,
      priorSchema: parsed.value.schema,
      files: encoded,
    });
    jsonText.value = JSON.stringify(result.schema, null, 2);
    procedureCheck.value = result.procedureCheck;
    warnings.value = [...localWarnings, ...(result.warnings || [])];
    dirty.value = false;
  } catch (err) {
    error.value = err?.message || "The course could not be regenerated.";
  } finally {
    loading.value = false;
  }
}

async function recheck() {
  error.value = parsed.value.error || schemaResult.value?.errors?.[0] || "";
  if (error.value) return;
  loading.value = true;
  try {
    const result = await generateCourse({
      mode: "check",
      prompt: course.value?.SourcePrompt || schemaResult.value.schema.meta.title,
      schema: schemaResult.value.schema,
    });
    jsonText.value = JSON.stringify(result.schema, null, 2);
    procedureCheck.value = result.procedureCheck;
    dirty.value = false;
    if (result.procedureCheck?.Conflicts && Object.keys(result.procedureCheck.Conflicts).length) {
      error.value = "The course still conflicts with the current handbook procedures.";
    }
  } catch (err) {
    error.value = err?.message || "The handbook check failed.";
  } finally {
    loading.value = false;
  }
}

async function save() {
  if (!savable.value) return false;
  saving.value = true;
  error.value = "";
  try {
    await updateCourse(db, course.value.ID, {
      schema: schemaResult.value.schema,
      prompt: course.value.SourcePrompt || "",
      procedureCheck: procedureCheck.value,
      userId: store.getters.userID,
    });
    return true;
  } catch (err) {
    error.value = err?.message || "The course could not be saved.";
    return false;
  } finally {
    saving.value = false;
  }
}

async function publish() {
  if (!savable.value) {
    error.value = "Recheck the course against the current handbooks before publishing.";
    return;
  }
  const saved = await save();
  if (!saved) return;
  await setCourseStatus(db, course.value.ID, "published");
}

async function unpublish() {
  await setCourseStatus(db, course.value.ID, "draft");
}

onMounted(async () => {
  await db.watch("Home/Courses", store);
  if (course.value?.Schema) {
    jsonText.value = JSON.stringify(course.value.Schema, null, 2);
    procedureCheck.value = course.value.ProcedureCheck || null;
  }
  loaded.value = true;
});
</script>
